import { createContext, ReactNode, useContext, useMemo } from 'react';
import { DataTableContextValue } from './types';

// ─────────────────────────────────────
// Context
// ─────────────────────────────────────

const DataTableContext = createContext<DataTableContextValue<unknown> | null>(
  null
);

// ─────────────────────────────────────
// Provider Props
// ─────────────────────────────────────

/** The context value's own state and actions; the provider derives `filteredRows` and `activeFilterCount`. */
type DataTableProviderProps<TData> = Omit<
  DataTableContextValue<TData>,
  'filteredRows' | 'activeFilterCount'
> & {
  children: ReactNode;
  /** Active filters the consumer holds outside `columnFilters` */
  panelFilterCount?: number;
};

// ─────────────────────────────────────
// Provider Component
// ─────────────────────────────────────

/**
 * Context provider for DataTable state management.
 * Provides table instance, filter state, column customization, and actions
 * to all child components via React Context.
 * @param {DataTableProviderProps} props - Provider configuration
 * @param {ReactNode} props.children - Child components
 * @param {Table} props.table - TanStack Table instance
 * @param {string} props.globalFilter - Current global filter value
 * @param {Function} props.setGlobalFilter - Global filter setter
 * @param {ColumnFiltersState} props.columnFilters - Column filter state
 * @param {Function} props.setColumnFilters - Column filters setter
 * @param {SortingState} props.sorting - Current sorting state
 * @param {Function} props.setSorting - Sorting setter
 * @param {VisibilityState} props.columnVisibility - Column visibility state
 * @param {Function} props.setColumnVisibility - Visibility setter
 * @param {string[]} props.columnOrder - Column order array
 * @param {Function} props.setColumnOrder - Column order setter
 * @param {ColumnPinningState} props.columnPinning - Column pinning state
 * @param {Function} props.setColumnPinning - Pinning setter
 * @param {ColumnSizingState} props.columnSizing - Column sizing state
 * @param {Function} props.setColumnSizing - Sizing setter
 * @param {Set<string>} [props.indeterminateRowIds] - Row ids whose checkbox shows partly selected
 * @param {boolean} props.isFilterPanelOpen - Filter panel open state
 * @param {Function} props.setIsFilterPanelOpen - Filter panel toggle
 * @param {boolean} props.isColumnFilterActive - Column filter row active state
 * @param {Function} props.setIsColumnFilterActive - Column filter toggle
 * @param {number} [props.panelFilterCount] - Active filters the consumer holds outside columnFilters
 * @param {Function} props.resetAllFilters - Reset all filters action
 * @param {Function} props.resetAllCustomizations - Reset all customizations action
 * @return {JSX.Element} The provider wrapper element
 */
export function DataTableProvider<TData>({
  children,
  table,
  globalFilter,
  setGlobalFilter,
  columnFilters,
  setColumnFilters,
  sorting,
  setSorting,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
  columnPinning,
  setColumnPinning,
  columnSizing,
  setColumnSizing,
  selectAll,
  toggledNodes,
  setSelectAll,
  setToggledNodes,
  selectionCount,
  fireSelectionChange,
  indeterminateRowIds,
  isFilterPanelOpen,
  setIsFilterPanelOpen,
  isColumnFilterActive,
  setIsColumnFilterActive,
  panelFilterCount = 0,
  resetAllFilters,
  resetAllCustomizations,
}: DataTableProviderProps<TData>) {
  // Derived values.
  // NB: do not memoize on `[table]` alone — the TanStack `table` reference is stable across
  // renders, so a `[table]` memo never recomputes when column/global filters change and leaves
  // `filteredRows` frozen at the initial unfiltered set (broke schedule-dialog "select all shown
  // rows", which read the stale value). `getFilteredRowModel()` is internally memoized by TanStack,
  // so calling it directly is cheap and always reflects the current filter state.
  const filteredRows = table.getFilteredRowModel().rows;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (globalFilter) count++;
    count += columnFilters.length;
    return count + panelFilterCount;
  }, [globalFilter, columnFilters, panelFilterCount]);

  const contextValue = useMemo<DataTableContextValue<TData>>(
    () => ({
      table,
      globalFilter,
      setGlobalFilter,
      columnFilters,
      setColumnFilters,
      sorting,
      setSorting,
      columnVisibility,
      setColumnVisibility,
      columnOrder,
      setColumnOrder,
      columnPinning,
      setColumnPinning,
      columnSizing,
      setColumnSizing,
      selectAll,
      toggledNodes,
      setSelectAll,
      setToggledNodes,
      selectionCount,
      fireSelectionChange,
      indeterminateRowIds,
      isFilterPanelOpen,
      setIsFilterPanelOpen,
      isColumnFilterActive,
      setIsColumnFilterActive,
      filteredRows,
      activeFilterCount,
      resetAllFilters,
      resetAllCustomizations,
    }),
    [
      table,
      globalFilter,
      setGlobalFilter,
      columnFilters,
      setColumnFilters,
      sorting,
      setSorting,
      columnVisibility,
      setColumnVisibility,
      columnOrder,
      setColumnOrder,
      columnPinning,
      setColumnPinning,
      columnSizing,
      setColumnSizing,
      selectAll,
      toggledNodes,
      setSelectAll,
      setToggledNodes,
      selectionCount,
      fireSelectionChange,
      indeterminateRowIds,
      isFilterPanelOpen,
      setIsFilterPanelOpen,
      isColumnFilterActive,
      setIsColumnFilterActive,
      filteredRows,
      activeFilterCount,
      resetAllFilters,
      resetAllCustomizations,
    ]
  );

  return (
    <DataTableContext.Provider
      value={contextValue as DataTableContextValue<unknown>}
    >
      {children}
    </DataTableContext.Provider>
  );
}

// ─────────────────────────────────────
// Hook
// ─────────────────────────────────────

/**
 * Hook to access DataTable context
 * Must be used within a DataTable component
 * @return {DataTableContextValue} The table context value
 */
export function useDataTableContext<TData>(): DataTableContextValue<TData> {
  const context = useContext(DataTableContext);

  if (!context) {
    throw new Error(
      'useDataTableContext must be used within a DataTableProvider'
    );
  }

  return context as DataTableContextValue<TData>;
}
